import Check from "@mui/icons-material/Check";
import { Box, Stack, Typography } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid-premium";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { TruncateTypography } from "components/TruncateTypography";
import { WasteTagsColumnHeader } from "components/WasteTagsColumnHeader";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { WasteLinesUnifiedQuery, WasteLinesUnifiedQueryVariables } from "generated-graphql/graphql";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { formatNumber } from "util/formatNumber";

import { WASTE_LINES_UNIFIED_QUERY } from "../../ManifestLineItems/api";
import { AddManifestButton } from "../../Manifests/AddManifestButton";
import { WasteStreamAmountCell, WasteStreamAmountHeader } from "../../WasteStreamAmountCell";
import { formatShipmentRangeDate } from "./formatShipmentRangeDate";
import { TileContentGreyBackground } from "./TileContentGreyBackground";
import { useFacilityWasteLineItemsDefaultSearch } from "./useFacilityWasteLineItemsDefaultSearch";
import { useHasWasteShipmentData } from "./useHasWasteShipmentData";
import { useWasteFacility } from "./useWasteFacility";
import { useWasteFacilityState } from "./WasteFacilityContext";
import { Filter } from "./WasteFacilityFilters";

type ManifestLineItemTileRow = NonNullable<
  NonNullable<WasteLinesUnifiedQuery["wasteLinesUnified"]>["items"]
>[number];

export const ManifestLineItemsTile = () => {
  return (
    <DashboardTile size={12}>
      <ManifestLineItemsTileContent />
    </DashboardTile>
  );
};

const ManifestLineItemsTileContent = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { tenantId } = useTenant();
  const { pushIdToUrl } = useSelectedIdInUrl("wasteLineUnifiedId", {
    prefixSegment: "manifest-line-items",
  });
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { data } = useWasteFacility(facilityId);

  const { startDate } = deferredFilters as Filter;

  const exportFilename = useMemo(
    () => (data.facility?.name ? `${data.facility.name}-manifest-line-items` : undefined),
    [data.facility?.name]
  );

  const defaultSearch = useFacilityWasteLineItemsDefaultSearch();
  const hasShipmentData = useHasWasteShipmentData();

  const formattedDateRange = useMemo(() => {
    return formatShipmentRangeDate({
      startDate: deferredFilters.startDate,
      endDate: deferredFilters.endDate,
    });
  }, [deferredFilters.startDate, deferredFilters.endDate]);

  return (
    <>
      <DashboardTile.Header
        title="Manifest Line Items"
        infoTooltip="See manifest line items for this facility in the date range. If line item is present in both vendor and e‑Manifest data, we display the vendor data unless otherwise noted."
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="textSecondary">
            {formattedDateRange}
          </Typography>
        </Stack>
      </DashboardTile.Header>
      <DashboardTile.ContentArea>
        {!hasShipmentData ? (
          <TileContentGreyBackground>
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ height: "100%", minHeight: 120 }}
            >
              <Typography variant="body1">🧾 No line item data to show</Typography>
              <AddManifestButton tenantId={tenantId ?? ""} facilityId={facilityId} size="small" />
            </Stack>
          </TileContentGreyBackground>
        ) : (
          <OmnisearchDataGrid<
            WasteLinesUnifiedQuery,
            WasteLinesUnifiedQueryVariables,
            ManifestLineItemTileRow
          >
            persistenceKey="facility-manifest-line-items-table"
            isURLDriven={false}
            columns={[
              {
                field: "lineItem",
                headerName: "Line Item",
                minWidth: 100,
                flex: 2,
                renderCell: ({ row: { willNotReport, wasteStreamWillNotReport }, value }) => {
                  // Mirror the top-level page: surface the source of exclusion
                  // so users can tell line-level from stream-level.
                  let label: string | null = null;
                  if (willNotReport === true) {
                    label = "Not Reportable";
                  } else if (wasteStreamWillNotReport === true) {
                    label = "Not Reportable (stream)";
                  }
                  return (
                    <Box>
                      <TruncateTypography variant="body2">{value}</TruncateTypography>
                      {label && (
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                      )}
                    </Box>
                  );
                },
              },
              {
                field: "profileNumber",
                headerName: "Profile #",
                flex: 0.5,
                renderCell: ({ value }) => {
                  return (
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <TruncateTypography variant="body2">{value}</TruncateTypography>
                    </Box>
                  );
                },
              },
              {
                field: "manifestTrackingNumber",
                headerName: "Manifest #",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                // Collapsed amount: the largest of the source amounts, with a
                // breakdown tooltip when sources disagree. The per-source columns
                // below stay available (hidden by default) for drill-down.
                field: "amount",
                headerName: "Amount",
                renderHeader: () => <WasteStreamAmountHeader />,
                flex: 0.5,
                minWidth: 110,
                sortable: false,
                filterable: false,
                renderCell: ({ row }) => (
                  <WasteStreamAmountCell
                    vendorAmount={row.vendorAmount}
                    eManifestAmount={row.eManifestAmount}
                    documentAmount={row.documentAmount}
                  />
                ),
              },
              {
                field: "vendorAmount",
                filterKeyType: "number",
                headerName: "Amount (Vendor)",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                valueGetter: (_, row) => {
                  const amount = formatNumber(row.vendorAmount);
                  return amount ? `${amount} lbs` : "";
                },
              },
              {
                field: "eManifestAmount",
                filterKeyType: "number",
                headerName: "Amount (e‑Manifest)",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                valueGetter: (_, row) => {
                  const amount = formatNumber(row.eManifestAmount);
                  return amount ? `${amount} lbs` : "";
                },
              },
              {
                field: "documentAmount",
                filterKeyType: "number",
                headerName: "Amount (Document)",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                valueGetter: (_, row) => {
                  const amount = formatNumber(row.documentAmount);
                  return amount ? `${amount} lbs` : "";
                },
              },
              {
                field: "shippedDate",
                filterable: false,
                headerName: "Ship Date",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                valueGetter: (_, row) => {
                  return getUtcDisplayDate(row.shippedDate, "med");
                },
              },
              {
                field: "dotDescription",
                headerName: "DOT Description",
                flex: 1,
                minWidth: 150,
                headerClassName: "wrapHeader",
              },
              {
                field: "managementMethodCode",
                headerName: "Management Method Code",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                field: "vendor",
                headerName: "Vendor",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                filterKeyType: "wasteVendor",
              },
              {
                field: "destinationFacilityName",
                headerName: "TSDF",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                field: "tags",
                filterKeyType: "wasteTags",
                renderHeader: () => <WasteTagsColumnHeader />,
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                renderCell: ({ row: { tags } }) => {
                  return <TagsDisplay tags={tags} />;
                },
              },
              {
                field: "willNotReport",
                headerName: "Not Reportable",
                filterKeyType: "boolean",
                sortable: false,
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                renderCell: ({ value }) => {
                  if (value === true) {
                    return <Check />;
                  }
                  return null;
                },
              },
            ]}
            // The backend never parses a documentAmount filter, so suppress the
            // auto-generated dead filter (the column still displays). Per ENG-15798.
            excludeFilterColumns={["documentAmount"]}
            dataQuery={WASTE_LINES_UNIFIED_QUERY}
            getItems={(data) => data.wasteLinesUnified?.items ?? []}
            getCount={(data) => data.wasteLinesUnified?.count ?? 0}
            initialPageSize={10}
            withPadding={false}
            rowHeight="auto"
            additionalFilterColumns={[
              {
                header: "Has Vendor Data",
                key: "isVendor",
                filterKeyType: "boolean",
              },
              {
                header: "e‑Manifest",
                key: "isEManifest",
                filterKeyType: "boolean",
              },
            ]}
            defaultSearch={defaultSearch}
            noDataMessage={"No manifest line items found for this facility."}
            initialSortModel={[{ field: "shippedDate", sort: "desc" }]}
            onRowClick={(params: GridRowParams<ManifestLineItemTileRow>) =>
              pushIdToUrl(params.row.id)
            }
            isRowSelectable={() => false}
            disableRowSelectionOnClick={true}
            initialColumnVisibilityModel={{
              dotDescription: false,
              // Collapsed into the single "Amount" column; available via manage columns.
              vendorAmount: false,
              eManifestAmount: false,
              documentAmount: false,
            }}
            commandButtons={({ search }) => [
              <AddManifestButton
                key="add-manifest"
                tenantId={tenantId ?? ""}
                facilityId={facilityId}
                size="small"
              />,
              <ExportDataButton
                timeoutInSeconds={180}
                key="export"
                iconOnly={true}
                exportType="wasteManifestLines"
                disabled={!facilityId || !startDate}
                tenantId={tenantId ?? ""}
                fileName={exportFilename}
                filter={{ search }}
              />,
            ]}
            columnVisibilityModel={{
              willNotReport: false,
            }}
          />
        )}
      </DashboardTile.ContentArea>
    </>
  );
};
